'use client'

import type { InteractiveButton } from '@/types/interactiveVideo'
import { FC } from 'react'
import { SurveySheetHost } from '../modal/ModalSurvey'
import type { SurveySheetState } from '../survey/useSurveySheet'
import { BranchButton } from './WideBranchOverlay'
import WideSurveyEntryButton from './WideSurveyEntryButton'
import { WIDE_FONT_FAMILY } from './wideFont'

type WideStackedPanelProps = {
  /** 選択肢を出すか（出す時点は映像に重ねる場合と同じ判定） */
  showChoices: boolean
  buttons: InteractiveButton[]
  questionText?: string | null
  defaultButtonColor?: string | null
  /** 任意アンケートの入口（残りの任意設問が無ければ null） */
  surveyEntry?: { total: number; onOpen: () => void } | null
  /** 展開中のアンケート（開いている間は選択肢の代わりにここへ出す） */
  survey?: SurveySheetState | null
  onSelect: (buttonId: string) => void | Promise<void>
}

/**
 * スマホ縦画面のワイド型で、動画の下に並べる選択肢・アンケートの欄。
 * 16:9 の映像は縦画面だと高さ 200px 前後しかなく、上に重ねると映像が隠れ文言も省略されるので、
 * 映像には何も重ねず、選択肢は 1 行ずつ文言を省略せずに並べる。
 */
const WideStackedPanel: FC<WideStackedPanelProps> = ({
  showChoices,
  buttons,
  questionText,
  defaultButtonColor,
  surveyEntry,
  survey,
  onSelect,
}) => {
  const isSurveyOpen = Boolean(survey?.expanded && survey.activeQuestion)
  const displayButtons = showChoices ? buttons.slice(0, 4) : []
  const showQuestion = Boolean(questionText) && displayButtons.length > 0

  if (!isSurveyOpen && displayButtons.length === 0 && !surveyEntry) return null

  return (
    <div
      data-testid="wide-stacked-panel"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        padding: '14px 12px 16px',
        background: '#0b1220',
        color: '#fff',
        fontFamily: WIDE_FONT_FAMILY,
      }}
    >
      {isSurveyOpen && survey ? (
        <div style={{ maxHeight: '60vh', overflowY: 'auto', borderRadius: '14px' }}>
          <SurveySheetHost survey={survey} />
        </div>
      ) : (
        <>
          {(showQuestion || surveyEntry) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <p
                style={{
                  flex: 1,
                  minWidth: 0,
                  margin: 0,
                  fontSize: '14px',
                  fontWeight: 800,
                  lineHeight: 1.4,
                }}
              >
                {showQuestion ? questionText : null}
              </p>
              {surveyEntry && (
                <WideSurveyEntryButton
                  total={surveyEntry.total}
                  onOpen={surveyEntry.onOpen}
                  style={{ flexShrink: 0 }}
                />
              )}
            </div>
          )}

          {displayButtons.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {displayButtons.map((button, index) => (
                <BranchButton
                  key={button.id}
                  button={button}
                  index={index}
                  compact={false}
                  variant="default"
                  defaultButtonColor={defaultButtonColor}
                  onSelect={onSelect}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default WideStackedPanel
